import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Search, Pencil, Trash2, Copy, X, Download, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageTitle, Card, Field, inputCls, GoldButton, GhostButton } from "@/admin/ui";

const empty = { title: "", content: "", category: "Geral" };

export const PromptsManager = () => {
  const [prompts, setPrompts] = useState([]);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("all");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/prompts").then(({ data }) => setPrompts(data));
  useEffect(() => { load(); }, []);

  const cats = useMemo(() => Array.from(new Set(prompts.map((p) => p.category))), [prompts]);
  const filtered = useMemo(
    () => prompts.filter((p) =>
      (p.title.toLowerCase().includes(query.toLowerCase()) || p.content.toLowerCase().includes(query.toLowerCase())) &&
      (cat === "all" || p.category === cat)),
    [prompts, query, cat],
  );

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/prompts/${editing.id}`, form);
      else await api.post("/admin/prompts", form);
      toast.success("Prompt salvo");
      setEditing(null);
      await load();
    } catch { toast.error("Erro ao salvar"); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Excluir "${p.title}"?`)) return;
    await api.delete(`/admin/prompts/${p.id}`); toast.success("Excluído"); load();
  };
  const duplicate = async (p) => { await api.post(`/admin/prompts/${p.id}/duplicate`); toast.success("Duplicado"); load(); };

  const exportAll = () => {
    const blob = new Blob([JSON.stringify(prompts, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "prompts-grupo-manga.json";
    a.click();
    toast.success("Prompts exportados");
  };

  const importFile = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const arr = JSON.parse(text);
      for (const p of arr) {
        await api.post("/admin/prompts", { title: p.title, content: p.content || "", category: p.category || "Geral" });
      }
      toast.success(`${arr.length} prompts importados`);
      load();
    } catch { toast.error("Arquivo inválido"); }
  };

  return (
    <div data-testid="admin-prompts">
      <PageTitle title="Gerenciador de Prompts" subtitle={`${prompts.length} prompts salvos`}
        action={
          <div className="flex gap-2">
            <GhostButton onClick={exportAll}><Download className="w-4 h-4 inline mr-1" /> Exportar</GhostButton>
            <GhostButton onClick={() => fileRef.current?.click()}><Upload className="w-4 h-4 inline mr-1" /> Importar</GhostButton>
            <GoldButton onClick={() => { setForm(empty); setEditing({}); }} data-testid="prompt-add-btn"><Plus className="w-4 h-4 inline mr-1" /> Novo</GoldButton>
          </div>
        } />
      <input ref={fileRef} type="file" accept="application/json" className="hidden" onChange={(e) => importFile(e.target.files[0])} />

      <Card className="mb-6 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Pesquisar prompts..." className={inputCls + " pl-11"} data-testid="prompt-search" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} className={inputCls + " md:w-56"}>
          <option value="all">Todas as Categorias</option>
          {cats.map((c) => (<option key={c} value={c}>{c}</option>))}
        </select>
      </Card>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((p) => (
          <Card key={p.id} className="flex flex-col" data-testid={`prompt-card-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <span className="text-[10px] uppercase tracking-wider text-[#F5C542]">{p.category}</span>
                <h4 className="font-display font-bold text-white mt-1">{p.title}</h4>
              </div>
            </div>
            <p className="text-sm text-white/50 mt-2 line-clamp-3 flex-1 whitespace-pre-wrap">{p.content}</p>
            <div className="flex gap-1 mt-4 pt-3 border-t border-white/[0.06]">
              <button onClick={() => { setForm({ title: p.title, content: p.content, category: p.category }); setEditing(p); }} className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg bg-white/[0.04] text-white/70 hover:text-[#F5C542] text-xs" data-testid={`prompt-edit-${p.id}`}><Pencil className="w-3.5 h-3.5" /> Editar</button>
              <button onClick={() => duplicate(p)} className="px-3 py-2 rounded-lg bg-white/[0.04] text-white/70 hover:text-white"><Copy className="w-3.5 h-3.5" /></button>
              <button onClick={() => remove(p)} className="px-3 py-2 rounded-lg bg-white/[0.04] text-white/70 hover:text-[#FF3B30]" data-testid={`prompt-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </Card>
        ))}
        {filtered.length === 0 && <Card className="col-span-full text-center py-12 text-white/40">Nenhum prompt.</Card>}
      </div>

      <AnimatePresence>
        {editing && (
          <motion.div className="fixed inset-0 z-[80] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={() => setEditing(null)} />
            <motion.form onSubmit={save} initial={{ scale: 0.94 }} animate={{ scale: 1 }} exit={{ scale: 0.94 }} className="relative glass-gold w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" data-testid="prompt-modal">
              <button type="button" onClick={() => setEditing(null)} className="absolute top-4 right-4 text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
              <h3 className="font-display text-xl font-bold text-white mb-5">{editing.id ? "Editar Prompt" : "Novo Prompt"}</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Título"><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="prompt-form-title" /></Field>
                  <Field label="Categoria"><input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} /></Field>
                </div>
                <Field label="Conteúdo (Markdown)">
                  <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={10} className={inputCls + " font-mono resize-y"} data-testid="prompt-form-content" />
                </Field>
              </div>
              <div className="flex gap-3 mt-6">
                <GoldButton type="submit" disabled={saving} className="flex-1" data-testid="prompt-save-btn">{saving ? "Salvando..." : "Salvar"}</GoldButton>
                <GhostButton type="button" onClick={() => setEditing(null)}>Cancelar</GhostButton>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
